import { Box, Chip, CircularProgress, Grid, styled, Typography } from '@material-ui/core';
import { useState } from 'react';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { useHistory } from 'react-router';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ClientActivitiesTable from './ClientActivitiesTable';
import { GridIcon, UnorderedListIcon } from '../../../../icons';
import ClientActivitiesGrid from './ClientActivitiesGrid';
import TablePager from '../../../../components/atom/TablePager';
import { useSorting } from '../../../../hooks/useSorting';
import { useSwaggerClient } from '../../../../hooks/useSwaggerClient';

const viewType = {
    LIST: 'LIST',
    GRID: 'GRID'
};

const operationTypes = {
    VENDOR: 'getActivitiesForAdmins',
    CLIENT: 'getAvailableActivitiesForClient'
};

const CustomToggleButton = styled(ToggleButton)(({ theme }) => ({
    color: theme.palette.primary.light,
    borderColor: theme.palette.primary.light,
    '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark
        }
    }
}));

const ClientActivitiesListPage = ({ clientId }) => {
    const history = useHistory();

    const [view, setView] = useState(viewType.LIST);
    const [operationId, setOperationId] = useState(operationTypes.VENDOR);

    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const { sort, sortDir, setSorting } = useSorting(['name', 'ASC']);

    const [data, loading, error] = useSwaggerClient('activities', operationId, {
        page,
        pageSize,
        sort,
        sortDir,
        clientId,
        search
    });

    const [clientData] = useSwaggerClient('clients', 'getClient', { clientId });

    const handleViewChange = (_, nextView) => {
        if (nextView !== null) {
            setView(nextView);
        }
    };

    const handleOperationChange = (_, nextOperation) => {
        if (nextOperation !== null) {
            setOperationId(nextOperation);
            setPage(0);
        }
    };

    const handleItemClick = activityId => {
        history.push(`/admin/activity/update/${activityId}`);
    };

    return (
        <Grid container spacing={2}>
            <AdminHeader
                title="Activities"
                search={search}
                onSearch={newSearch => {
                    setPage(0);
                    setSearch(newSearch);
                }}
                childrenBefore
            >
                <ToggleButtonGroup
                    size="small"
                    value={operationId}
                    exclusive
                    onChange={handleOperationChange}
                >
                    <CustomToggleButton value={operationTypes.VENDOR}>
                        Vendor inventory
                    </CustomToggleButton>
                    <CustomToggleButton value={operationTypes.CLIENT}>
                        Available for Client
                    </CustomToggleButton>
                </ToggleButtonGroup>
            </AdminHeader>
            <Grid item container xs={12} justifyContent="center">
                {loading ? (
                    <Box
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            display: 'flex',
                            width: '100%',
                            height: '100%'
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : data && data.total > 0 ? (
                    <>
                        {data.visibleIslands && (
                            <Box
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    width: '100%',
                                    alignItems: 'flex-start',
                                    flexDirection: 'column',
                                    marginBottom: '16px'
                                }}
                            >
                                <Typography variant="caption" style={{ marginBottom: '4px' }}>
                                    Based on the{' '}
                                    <span style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                                        Visible islands
                                    </span>{' '}
                                    setting:
                                </Typography>
                                <Box
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}
                                >
                                    {data.visibleIslands.map(island => (
                                        <Chip key={island} label={island} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <Box
                            container
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            justifyItems="center"
                            width="100%"
                            mb={2}
                        >
                            <Box>
                                <ToggleButtonGroup
                                    size="small"
                                    value={view}
                                    exclusive
                                    onChange={handleViewChange}
                                >
                                    <CustomToggleButton value={viewType.LIST}>
                                        <UnorderedListIcon />
                                    </CustomToggleButton>
                                    <CustomToggleButton value={viewType.GRID}>
                                        <GridIcon />
                                    </CustomToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <div style={{ marginRight: '-25px' }}>
                                <TablePager
                                    total={data?.total}
                                    page={page}
                                    setPage={setPage}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                />
                            </div>
                        </Box>
                        {view === viewType.LIST ? (
                            <ClientActivitiesTable
                                activities={data?.activities}
                                setSorting={setSorting}
                                sortDir={sortDir}
                                sort={sort}
                                handleItemClick={handleItemClick}
                                isClientView={operationId === operationTypes.CLIENT}
                                clientKey={clientData?.client?.client_key}
                            />
                        ) : (
                            <ClientActivitiesGrid
                                activities={data?.activities}
                                handleItemClick={handleItemClick}
                            />
                        )}
                    </>
                ) : (
                    <Box width={'100%'} style={{ marginTop: '16px' }}>
                        <Alert severity={error ? 'error' : 'info'}>
                            {error
                                ? 'There was an error loading activities. Please try again later.'
                                : `No activities found.${
                                      search ? ' Please try a different search.' : ''
                                  }`}
                        </Alert>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default ClientActivitiesListPage;
